/** @format */

import React from "react";
import { Box } from "./MuiCompat";
import { Link } from "react-router-dom";
import { trackEvent, trackOutboundLink, EventNames } from "../utils/analytics";
import { APP_STORE_URLS, STORE_TYPES } from "../utils/constants";
import appStoreBadge from "../assets/images/app-store-download-button-in-white.png";
import playStoreBadge from "../assets/images/google-play-store-download-button-in-white.png";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;
const fontMono = `'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;

const microLabel = {
  fontFamily: fontMono,
  fontSize: "10.5px",
  fontWeight: 500,
  letterSpacing: "0.22em",
  textTransform: "uppercase" as const,
  color: "rgba(255, 255, 255, 0.55)",
};

const headingStyle = {
  m: 0,
  fontFamily: fontInter,
  fontWeight: 600,
  fontSize: "clamp(1.9rem, 3.4vw, 2.7rem)",
  lineHeight: 1.05,
  letterSpacing: "-0.022em",
  color: "var(--color-snow)",
  textAlign: "center" as const,
};

const bodyStyle = {
  fontFamily: fontInter,
  fontSize: "clamp(0.98rem, 1.15vw, 1.08rem)",
  fontWeight: 400,
  lineHeight: 1.55,
  color: "rgba(255, 255, 255, 0.66)",
  textAlign: "center" as const,
};

const partnerTypes = [
  "Brands",
  "Coaches",
  "Wearables",
  "Platforms",
  "Research",
];

// Glass card base + per-side identity. The two cards share scaffolding so the
// section reads as parallel choices, but each carries its own atmospheric
// color (cyan = personal/AI, violet = pro/premium per DESIGN.md).
const glassCard = (accent: string) => ({
  position: "relative" as const,
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  textAlign: "center" as const,
  px: { xs: 4, sm: 5, md: 6 },
  py: { xs: 6, md: 8 },
  borderRadius: "20px",
  border: `1px solid rgba(${accent}, 0.18)`,
  background:
    "linear-gradient(180deg, rgba(17, 18, 20, 0.55) 0%, rgba(7, 8, 10, 0.7) 100%)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  boxShadow: `
    0 30px 80px rgba(0, 0, 0, 0.55),
    0 0 60px rgba(${accent}, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.05)
  `,
  overflow: "hidden",
});

const PartnerSection: React.FC = () => {
  const handleAppStoreClick = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "Partner Section App Store Download",
      button_location: "partner_section",
      store_type: STORE_TYPES.APPLE,
      destination_url: APP_STORE_URLS.APPLE_APP_STORE,
    });
    trackOutboundLink(
      APP_STORE_URLS.APPLE_APP_STORE,
      "Partner Section App Store Download",
      STORE_TYPES.APPLE,
    );
  };

  const handlePlayStoreClick = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "Partner Section Play Store Download",
      button_location: "partner_section",
      store_type: STORE_TYPES.GOOGLE,
      destination_url: APP_STORE_URLS.GOOGLE_PLAY_STORE,
    });
    trackOutboundLink(
      APP_STORE_URLS.GOOGLE_PLAY_STORE,
      "Partner Section Play Store Download",
      STORE_TYPES.GOOGLE,
    );
  };

  const handleContactClick = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "Partner Contact",
      button_location: "partner_section",
      destination_url: "/contact",
    });
  };

  const badgeLinkStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
  };

  const badgeImgStyle: React.CSSProperties = {
    height: 72,
    width: "auto",
    display: "block",
  };

  // sky-signal cyan and violet-haze RGB values for per-card identity
  const CYAN = "86, 194, 255";
  const VIOLET = "169, 119, 255";

  // Reusable atmospheric halo positioned behind each card's icon, giving the
  // card a glowing "light source" rather than a flat tinted background.
  const cardAtmosphere = (accent: string) => (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: "50%",
        top: 0,
        transform: "translate(-50%, -30%)",
        width: "120%",
        height: "70%",
        background: `radial-gradient(60% 50% at 50% 50%, rgba(${accent}, 0.22) 0%, rgba(${accent}, 0) 70%)`,
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );

  // Tinted icon medallion at the top of each card — feels like a physical key
  // that anchors the column's identity.
  const iconMedallion = (icon: React.ReactNode, accent: string) => (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 68,
        height: 68,
        borderRadius: "18px",
        backgroundColor: `rgba(${accent}, 0.1)`,
        border: `1px solid rgba(${accent}, 0.28)`,
        color: `rgb(${accent})`,
        boxShadow: `
          0 0 36px rgba(${accent}, 0.35),
          inset 0 1px 0 rgba(255, 255, 255, 0.08)
        `,
        mb: { xs: 3, md: 4 },
      }}
    >
      {icon}
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        py: { xs: 12, md: 18 },
        overflow: "hidden",
      }}
    >
      {/* Section atmosphere: dual radial — sky on the left half, violet on
          the right half — so each card sits in its own pool of light. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(40% 60% at 28% 50%, rgba(${CYAN}, 0.16) 0%, transparent 70%),
            radial-gradient(40% 60% at 72% 50%, rgba(${VIOLET}, 0.16) 0%, transparent 70%)
          `,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          maxWidth: "var(--page-max-width)",
          mx: "auto",
          px: { xs: 3, sm: 5, md: 8 },
        }}
      >
        {/* Lead-in: a single eyebrow + brief framing for the dichotomy.
            Centered to mirror the cards beneath it. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: { xs: 7, md: 10 },
          }}
        >
          <Box sx={{ ...microLabel, color: "rgba(255,255,255,0.45)" }}>
            Two ways in
          </Box>
          <Box
            component="h2"
            sx={{
              m: 0,
              mt: 2,
              maxWidth: "780px",
              textAlign: "center",
              fontFamily: fontInter,
              fontWeight: 600,
              fontSize: "clamp(2rem, 4.6vw, 3.4rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.024em",
              color: "var(--color-snow)",
            }}
          >
            Wherever you fit, EON meets you there.
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: { md: 4, lg: 5 },
            rowGap: { xs: 4, md: 0 },
            alignItems: "stretch",
          }}
        >
          {/* ============= LEFT — Download CTA (cyan identity) ============= */}
          <Box sx={glassCard(CYAN)}>
            {cardAtmosphere(CYAN)}

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              {iconMedallion(
                <PhoneIphoneOutlinedIcon sx={{ fontSize: 32 }} />,
                CYAN,
              )}

              <Box sx={{ ...microLabel, color: `rgba(${CYAN}, 0.9)` }}>
                For you
              </Box>

              <Box component="h3" sx={{ ...headingStyle, mt: 1.5 }}>
                Try out EON <br />
              </Box>

              <Box sx={{ ...bodyStyle, mt: { xs: 2.5, md: 3 }, maxWidth: 420 }}>
                Download once. Set your goals and constraints. Your agent starts
                learning in the background across your goals, your data, the
                loops in your life.
              </Box>

              <Box
                sx={{
                  mt: { xs: 4, md: 5 },
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  alignItems: "center",
                }}
              >
                <a
                  href={APP_STORE_URLS.APPLE_APP_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppStoreClick}
                  aria-label="Download on the App Store"
                  title="Download on the App Store"
                  style={badgeLinkStyle}
                >
                  <img
                    src={appStoreBadge}
                    alt="Download on the App Store"
                    style={badgeImgStyle}
                  />
                </a>
                <a
                  href={APP_STORE_URLS.GOOGLE_PLAY_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handlePlayStoreClick}
                  aria-label="Get it on Google Play"
                  title="Get it on Google Play"
                  style={badgeLinkStyle}
                >
                  <img
                    src={playStoreBadge}
                    alt="Get it on Google Play"
                    style={badgeImgStyle}
                  />
                </a>
              </Box>
            </Box>
          </Box>

          {/* ============= RIGHT — Partner CTA (violet identity) ============= */}
          <Box sx={glassCard(VIOLET)}>
            {cardAtmosphere(VIOLET)}

            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
              }}
            >
              {iconMedallion(
                <HandshakeOutlinedIcon sx={{ fontSize: 32 }} />,
                VIOLET,
              )}

              <Box sx={{ ...microLabel, color: `rgba(${VIOLET}, 0.9)` }}>
                Build with us
              </Box>

              <Box component="h3" sx={{ ...headingStyle, mt: 1.5 }}>
                Bring EON to <br />
                the people you serve.
              </Box>

              <Box sx={{ ...bodyStyle, mt: { xs: 2.5, md: 3 }, maxWidth: 460 }}>
                For brands, coaches, and platforms in health and wellness. Plug
                your customers into a multiplayer agent network. Privately, with
                their consent, and the outcomes they actually feel.
              </Box>

              <Box sx={{ mt: { xs: 4, md: 5 } }}>
                <Link
                  to="/contact"
                  onClick={handleContactClick}
                  style={{ textDecoration: "none" }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "10px",
                      px: "22px",
                      py: "13px",
                      borderRadius: "10px",
                      backgroundColor: "var(--color-ash-50)",
                      color: "#2F3031",
                      fontFamily: fontInter,
                      fontSize: "15px",
                      fontWeight: 500,
                      letterSpacing: "0.005em",
                      boxShadow:
                        "0 8px 24px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
                      transition:
                        "transform 200ms ease, background-color 200ms ease, box-shadow 200ms ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        backgroundColor: "#FFFFFF",
                        boxShadow:
                          "0 12px 30px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
                      },
                      "&:hover .arrow": {
                        transform: "translateX(2px)",
                      },
                    }}
                  >
                    Contact us
                    <Box
                      component="span"
                      className="arrow"
                      aria-hidden
                      sx={{
                        display: "inline-block",
                        transition: "transform 200ms ease",
                      }}
                    >
                      →
                    </Box>
                  </Box>
                </Link>
              </Box>

              <Box
                sx={{
                  mt: { xs: 5, md: 6 },
                  pt: 3,
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: { xs: "12px", md: "18px" },
                  borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                  width: "100%",
                }}
              >
                <Box sx={{ ...microLabel, color: "rgba(255, 255, 255, 0.4)" }}>
                  We work with
                </Box>
                {partnerTypes.map((type) => (
                  <Box
                    key={type}
                    sx={{
                      fontFamily: fontMono,
                      fontSize: "11px",
                      fontWeight: 500,
                      letterSpacing: "0.08em",
                      color: "rgba(255, 255, 255, 0.62)",
                    }}
                  >
                    {type}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerSection;
